import { InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';
import { interactiveScopes } from './msal.ts';

const gatewayUrl = import.meta.env.VITE_GATEWAY_URL as string;

/** Acquire a token silently; fall back to a popup when the session has expired or silent iframe times out. */
async function acquireToken(instance: ReturnType<typeof useMsal>['instance']): Promise<string> {
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  if (!account) throw new Error('not_authenticated');
  try {
    const result = await instance.acquireTokenSilent({ account, scopes: interactiveScopes });
    return result.accessToken;
  } catch (err) {
    // InteractionRequiredAuthError: session expired or consent needed.
    // BrowserAuthError (monitor_window_timeout): silent iframe blocked by browser cookie policy.
    // In both cases, fall back to an interactive popup.
    if (err instanceof InteractionRequiredAuthError || err instanceof BrowserAuthError) {
      const result = await instance.acquireTokenPopup({ scopes: interactiveScopes });
      return result.accessToken;
    }
    throw err;
  }
}

export function useApi() {
  const { instance } = useMsal();

  const request = useCallback(
    async <T = unknown>(path: string, init: RequestInit = {}): Promise<T> => {
      const accessToken = await acquireToken(instance);
      const headers: Record<string, string> = {
        ...(init.headers as Record<string, string> | undefined),
        Authorization: `Bearer ${accessToken}`,
      };
      if (init.body) headers['Content-Type'] = 'application/json';
      const res = await fetch(`${gatewayUrl}${path}`, { ...init, headers });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status} ${text}`);
      }
      if (res.status === 204) return undefined as T;
      return (await res.json()) as T;
    },
    [instance]
  );

  const openStream = useCallback(
    async (path: string): Promise<EventSource> => {
      const accessToken = await acquireToken(instance);
      // EventSource can't set headers, so we send the token as a query param.
      // Gateway accepts `?access_token=` as an alternative.
      const url = new URL(`${gatewayUrl}${path}`);
      url.searchParams.set('access_token', accessToken);
      return new EventSource(url.toString(), { withCredentials: false });
    },
    [instance]
  );

  return { request, openStream };
}
